import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "availableList",
    "availableCount",
    "selectedList",
    "hiddenInputs",
    "ordersCount",
    "stopsCount",
    "distanceEstimate",
    "durationEstimate",
    "emptyState",
    "form",
    "warehouseSelect",
    "vehicleSelect",
    "returnToBaseInput",
    "returnModeLabel",
    "searchInput",
    "map"
  ]

  static values = {
    estimateUrl: String,
    optimizeUrl: String
  }

  connect() {
    this.selectedOrders = []
    this.estimateTimeout = null
    this.originLat = null
    this.originLng = null
    this.map = null
    this.routeLine = null
    this.markers = []

    console.log("[routes] controller conectado")
    console.log("[routes] estimateUrlValue:", this.estimateUrlValue)
    console.log("[routes] optimizeUrlValue:", this.optimizeUrlValue)

    if (!this.hasSelectedListTarget) {
      console.warn("[routes] Target selectedList não encontrado")
      return
    }

    this.updateOriginFromWarehouse()
    this.syncReturnToBaseFromVehicle()
    this.updateReturnModeLabel()
    this.initializeMap()
    this.refresh()
  }

  disconnect() {
    clearTimeout(this.estimateTimeout)

    if (this.map) {
      this.map.remove()
      this.map = null
    }
  }

  warehouseChanged() {
    this.updateOriginFromWarehouse()
    this.refresh()
  }

  vehicleChanged() {
    this.syncReturnToBaseFromVehicle()
    this.updateReturnModeLabel()
    this.refresh()
  }

  returnToBaseChanged() {
    this.updateReturnModeLabel()
    this.refresh()
  }

  updateOriginFromWarehouse() {
    if (!this.hasWarehouseSelectTarget) {
      this.originLat = null
      this.originLng = null
      return
    }

    const option = this.warehouseSelectTarget.selectedOptions[0]

    if (!option || !option.value) {
      this.originLat = null
      this.originLng = null
      return
    }

    const lat = parseFloat(option.dataset.latitude)
    const lng = parseFloat(option.dataset.longitude)

    this.originLat = Number.isFinite(lat) ? lat : null
    this.originLng = Number.isFinite(lng) ? lng : null
  }

  syncReturnToBaseFromVehicle() {
    if (!this.hasVehicleSelectTarget || !this.hasReturnToBaseInputTarget) return

    const option = this.vehicleSelectTarget.selectedOptions[0]
    if (!option || !option.value) return

    this.returnToBaseInputTarget.checked = String(option.dataset.returnToBase) === "1"
  }

  updateReturnModeLabel() {
    if (!this.hasReturnModeLabelTarget || !this.hasReturnToBaseInputTarget) return

    this.returnModeLabelTarget.textContent = this.returnToBaseInputTarget.checked
      ? "Volta para a base"
      : "Encerra no último ponto"
  }

  selectedVehicleMaxStops() {
    if (!this.hasVehicleSelectTarget) return null
    const option = this.vehicleSelectTarget.selectedOptions[0]
    if (!option || !option.value) return null

    const value = parseInt(option.dataset.maxStops, 10)
    return Number.isFinite(value) ? value : null
  }

  selectedVehicleMaxDistanceKm() {
    if (!this.hasVehicleSelectTarget) return null
    const option = this.vehicleSelectTarget.selectedOptions[0]
    if (!option || !option.value) return null

    const value = parseFloat(option.dataset.maxDistanceKm)
    return Number.isFinite(value) ? value : null
  }

  initializeMap() {
    if (!this.hasMapTarget) return
    if (typeof L === "undefined") {
      console.error("[routes] Leaflet não carregado")
      return
    }
    if (this.map) return

    const initialLat = Number.isFinite(this.originLat) ? this.originLat : -23.55052
    const initialLng = Number.isFinite(this.originLng) ? this.originLng : -46.63331

    this.map = L.map(this.mapTarget).setView([initialLat, initialLng], 11)

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "&copy; OpenStreetMap contributors"
    }).addTo(this.map)

    if (Number.isFinite(this.originLat) && Number.isFinite(this.originLng)) {
      this.drawOriginOnly()
    }

    setTimeout(() => {
      if (this.map) this.map.invalidateSize()
    }, 200)
  }

  originIcon() {
    return L.divIcon({
      className: "custom-origin-marker",
      html: `<div class="marker-origin">O</div>`,
      iconSize: [34, 34],
      iconAnchor: [17, 17]
    })
  }

  stopIcon(index) {
    return L.divIcon({
      className: "custom-numbered-marker",
      html: `<div class="marker-pin">${index + 1}</div>`,
      iconSize: [30, 30],
      iconAnchor: [15, 15]
    })
  }

  drawOriginOnly() {
    if (!this.map) return

    this.clearMapLayers()

    if (!Number.isFinite(this.originLat) || !Number.isFinite(this.originLng)) return

    const originMarker = L.marker([this.originLat, this.originLng], {
      icon: this.originIcon()
    })
      .addTo(this.map)
      .bindPopup("Origem da rota")

    this.markers.push(originMarker)
    this.map.setView([this.originLat, this.originLng], 12)
  }

  clearMapLayers() {
    if (this.routeLine && this.map) {
      this.map.removeLayer(this.routeLine)
      this.routeLine = null
    }

    this.markers.forEach(marker => {
      if (this.map) this.map.removeLayer(marker)
    })
    this.markers = []
  }

  drawRoute(geometry) {
    if (!this.map) return
    if (!geometry || !Array.isArray(geometry.coordinates)) {
      console.warn("[routes] geometry inválida para desenhar rota")
      return
    }

    this.clearMapLayers()

    const latLngs = geometry.coordinates.map(coord => [coord[1], coord[0]])

    this.routeLine = L.polyline(latLngs, {
      weight: 5,
      opacity: 0.85
    }).addTo(this.map)

    if (Number.isFinite(this.originLat) && Number.isFinite(this.originLng)) {
      const originMarker = L.marker([this.originLat, this.originLng], {
        icon: this.originIcon()
      })
        .addTo(this.map)
        .bindPopup("Origem da rota")

      this.markers.push(originMarker)
    }

    this.selectedOrders.forEach((order, index) => {
      if (Number.isFinite(order.latitude) && Number.isFinite(order.longitude)) {
        const marker = L.marker([order.latitude, order.longitude], {
          icon: this.stopIcon(index)
        })
          .addTo(this.map)
          .bindPopup(`Parada ${index + 1} - Pedido #${order.id}`)

        this.markers.push(marker)
      }
    })

    if (this.routeLine.getBounds().isValid()) {
      this.map.fitBounds(this.routeLine.getBounds(), { padding: [30, 30] })
    }
  }

  addOrder(event) {
    const maxStops = this.selectedVehicleMaxStops()
    if (maxStops && this.selectedOrders.length >= maxStops) {
      alert(`Este veículo permite no máximo ${maxStops} paradas.`)
      return
    }

    const card = event.currentTarget.closest(".order-card")
    if (!card) return

    const orderId = card.dataset.orderId
    if (!orderId) return

    if (this.selectedOrders.find(item => item.id === orderId)) return

    const latitude = parseFloat(card.dataset.latitude)
    const longitude = parseFloat(card.dataset.longitude)

    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
      alert("Este pedido não possui latitude/longitude válida para cálculo da rota.")
      return
    }

    const cloned = card.cloneNode(true)
    cloned.classList.add("selected-order-item")

    const button = cloned.querySelector("button")
    if (button) {
      button.textContent = "Remover"
      button.classList.remove("btn-outline-primary")
      button.classList.add("btn-outline-danger")
      button.setAttribute("data-action", "click->routes#removeOrder")
    }

    this.selectedOrders.push({
      id: orderId,
      latitude: latitude,
      longitude: longitude,
      warehouseId: card.dataset.warehouseId || null,
      html: cloned.outerHTML
    })

    card.remove()
    this.refresh()
  }

  removeOrder(event) {
    const card = event.currentTarget.closest(".order-card")
    if (!card) return

    const orderId = card.dataset.orderId
    this.selectedOrders = this.selectedOrders.filter(item => item.id !== orderId)

    const button = card.querySelector("button")
    if (button) {
      button.textContent = "Adicionar"
      button.classList.remove("btn-outline-danger")
      button.classList.add("btn-outline-primary")
      button.setAttribute("data-action", "click->routes#addOrder")
    }

    if (this.hasAvailableListTarget) {
      this.availableListTarget.appendChild(card)
    }

    this.refresh()
  }

  filterOrders() {
    if (!this.hasAvailableListTarget) return

    const term = this.hasSearchInputTarget
      ? this.searchInputTarget.value.toLowerCase()
      : ""

    const selectedWarehouseId = this.hasWarehouseSelectTarget
      ? this.warehouseSelectTarget.value
      : ""

    this.availableListTarget.querySelectorAll(".order-card").forEach(card => {
      const text = (card.dataset.searchText || "").toLowerCase()
      const cardWarehouseId = card.dataset.warehouseId || ""

      const matchesText = text.includes(term)
      const matchesWarehouse =
        !selectedWarehouseId || String(cardWarehouseId) === String(selectedWarehouseId)

      card.style.display = matchesText && matchesWarehouse ? "" : "none"
    })

    this.updateAvailableCount()
  }

  updateAvailableCount() {
    if (!this.hasAvailableListTarget || !this.hasAvailableCountTarget) return

    const visibleCards = Array.from(
      this.availableListTarget.querySelectorAll(".order-card")
    ).filter(card => card.style.display !== "none")

    this.availableCountTarget.textContent = visibleCards.length
  }

  refresh() {
    if (!this.hasSelectedListTarget) return

    this.renderSelected()
    this.renderHiddenInputs()
    this.updateCounters()
    this.filterOrders()

    clearTimeout(this.estimateTimeout)
    this.estimateTimeout = setTimeout(() => {
      this.calculateRouteEstimate()
    }, 300)
  }

  renderSelected() {
    if (!this.hasSelectedListTarget) return

    this.selectedListTarget.innerHTML = ""

    if (this.selectedOrders.length === 0) {
      this.selectedListTarget.innerHTML = `
        <div class="text-muted text-center py-5">
          Nenhum pedido adicionado à rota.
        </div>
      `
      return
    }

    this.selectedOrders.forEach((item, index) => {
      const wrapper = document.createElement("div")
      wrapper.className = "mb-2"
      wrapper.innerHTML = `
        <div class="d-flex align-items-center">
          <div class="badge bg-secondary me-2">${index + 1}</div>
          <div class="flex-grow-1">
            ${item.html}
          </div>
          <div class="d-flex flex-column ms-2">
            <button type="button"
                    class="btn btn-sm btn-light mb-1"
                    data-index="${index}"
                    data-action="click->routes#moveUp">↑</button>
            <button type="button"
                    class="btn btn-sm btn-light"
                    data-index="${index}"
                    data-action="click->routes#moveDown">↓</button>
          </div>
        </div>
      `
      this.selectedListTarget.appendChild(wrapper)
    })
  }

  renderHiddenInputs() {
    if (!this.hasHiddenInputsTarget) return

    this.hiddenInputsTarget.innerHTML = ""

    if (this.hasWarehouseSelectTarget) {
      const warehouseInput = document.createElement("input")
      warehouseInput.type = "hidden"
      warehouseInput.name = "warehouse_id"
      warehouseInput.value = this.warehouseSelectTarget.value || ""
      this.hiddenInputsTarget.appendChild(warehouseInput)
    }

    this.selectedOrders.forEach(item => {
      const input = document.createElement("input")
      input.type = "hidden"
      input.name = "order_ids[]"
      input.value = item.id
      this.hiddenInputsTarget.appendChild(input)
    })
  }

  updateCounters() {
    const total = this.selectedOrders.length

    if (this.hasOrdersCountTarget) {
      this.ordersCountTarget.textContent = total
    }

    if (this.hasStopsCountTarget) {
      this.stopsCountTarget.textContent = total
    }

    if (this.hasDistanceEstimateTarget) {
      this.distanceEstimateTarget.textContent = total > 0 ? "Calculando..." : "-"
    }

    if (this.hasDurationEstimateTarget) {
      this.durationEstimateTarget.textContent = "00:00"
    }
  }

  buildPayload(validStops) {
    return {
      origin: {
        latitude: this.originLat,
        longitude: this.originLng
      },
      return_to_base: this.hasReturnToBaseInputTarget
        ? this.returnToBaseInputTarget.checked
        : false,
      stops: validStops.map(order => ({
        id: order.id,
        latitude: order.latitude,
        longitude: order.longitude
      }))
    }
  }

  async calculateRouteEstimate() {
    if (!this.hasDistanceEstimateTarget) return

    if (this.selectedOrders.length === 0) {
      this.distanceEstimateTarget.textContent = "-"
      if (this.hasDurationEstimateTarget) this.durationEstimateTarget.textContent = "00:00"
      this.drawOriginOnly()
      return
    }

    if (!this.hasEstimateUrlValue || !this.estimateUrlValue) {
      this.distanceEstimateTarget.textContent = "Sem endpoint"
      if (this.hasDurationEstimateTarget) this.durationEstimateTarget.textContent = "00:00"
      return
    }

    if (!Number.isFinite(this.originLat) || !Number.isFinite(this.originLng)) {
      this.distanceEstimateTarget.textContent = "Origem inválida"
      if (this.hasDurationEstimateTarget) this.durationEstimateTarget.textContent = "00:00"
      return
    }

    const validStops = this.selectedOrders.filter(order =>
      Number.isFinite(order.latitude) && Number.isFinite(order.longitude)
    )

    if (validStops.length === 0) {
      this.distanceEstimateTarget.textContent = "-"
      if (this.hasDurationEstimateTarget) this.durationEstimateTarget.textContent = "00:00"
      this.drawOriginOnly()
      return
    }

    const payload = this.buildPayload(validStops)

    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

      const response = await fetch(this.estimateUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": csrfToken
        },
        body: JSON.stringify(payload)
      })

      if (!response.ok) {
        const text = await response.text()
        console.error("[routes] erro do backend:", text)
        throw new Error(`Erro HTTP ${response.status}`)
      }

      const data = await response.json()

      if (this.hasDistanceEstimateTarget) {
        this.distanceEstimateTarget.textContent =
          data.distance_km != null ? `${data.distance_km} km` : "-"
      }

      if (this.hasDurationEstimateTarget) {
        this.durationEstimateTarget.textContent = data.duration_text || "00:00"
      }

      const maxDistanceKm = this.selectedVehicleMaxDistanceKm()
      if (maxDistanceKm && data.distance_km && Number(data.distance_km) > maxDistanceKm) {
        this.distanceEstimateTarget.textContent = `${data.distance_km} km ⚠`
      }

      if (data.geometry) {
        this.drawRoute(data.geometry)
      } else {
        this.drawOriginOnly()
      }
    } catch (error) {
      console.error("[routes] erro ao calcular estimativa:", error)

      if (this.hasDistanceEstimateTarget) {
        this.distanceEstimateTarget.textContent = "Erro"
      }

      if (this.hasDurationEstimateTarget) {
        this.durationEstimateTarget.textContent = "00:00"
      }
    }
  }

  async optimize(event) {
    const button = event?.currentTarget
    const originalText = button ? button.innerHTML : null

    if (this.selectedOrders.length <= 1) {
      alert("Adicione pelo menos 2 pedidos para otimizar a sequência.")
      return
    }

    if (!this.hasOptimizeUrlValue || !this.optimizeUrlValue) {
      alert("Endpoint de otimização não configurado.")
      return
    }

    if (!Number.isFinite(this.originLat) || !Number.isFinite(this.originLng)) {
      alert("Selecione um ponto de início válido.")
      return
    }

    const payload = this.buildPayload(this.selectedOrders)

    try {
      if (button) {
        button.disabled = true
        button.innerHTML = "Otimizando..."
      }

      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

      const response = await fetch(this.optimizeUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": csrfToken
        },
        body: JSON.stringify(payload)
      })

      if (!response.ok) {
        const text = await response.text()
        console.error("[routes] erro ao otimizar:", text)
        throw new Error(`Erro HTTP ${response.status}`)
      }

      const data = await response.json()

      if (!Array.isArray(data.ordered_ids) || data.ordered_ids.length === 0) {
        throw new Error("OSRM não retornou ordem otimizada")
      }

      const reordered = []
      data.ordered_ids.forEach(id => {
        const found = this.selectedOrders.find(order => String(order.id) === String(id))
        if (found) reordered.push(found)
      })

      if (reordered.length === this.selectedOrders.length) {
        this.selectedOrders = reordered
      }

      this.renderSelected()
      this.renderHiddenInputs()
      this.updateAvailableCount()
      this.updateCounters()

      // após otimizar, recalcula a rota final considerando retorno à base
      await this.calculateRouteEstimate()
    } catch (error) {
      console.error("[routes] erro ao otimizar sequência:", error)
      alert("Não foi possível otimizar a sequência da rota.")
    } finally {
      if (button) {
        button.disabled = false
        button.innerHTML = originalText
      }
    }
  }

  moveUp(event) {
    const index = parseInt(event.currentTarget.dataset.index, 10)
    if (index <= 0) return

    ;[this.selectedOrders[index - 1], this.selectedOrders[index]] = [
      this.selectedOrders[index],
      this.selectedOrders[index - 1]
    ]

    this.refresh()
  }

  moveDown(event) {
    const index = parseInt(event.currentTarget.dataset.index, 10)
    if (index >= this.selectedOrders.length - 1) return

    ;[this.selectedOrders[index], this.selectedOrders[index + 1]] = [
      this.selectedOrders[index + 1],
      this.selectedOrders[index]
    ]

    this.refresh()
  }
}